import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'data-upload-how',
    category: 'data',
    title: 'Caricare un file, passo per passo',
    summary: 'Come caricare un CSV e leggere il risultato della validazione.',
    body: (
      <>
        <p>Quando il tuo file CSV è pronto, caricalo dall'interno della comunità:</p>
        <ol>
          <li>Apri la tua comunità e scegli <strong>Gestione Dati</strong> nel menu a sinistra.</li>
          <li>In <strong>Seleziona tipo di dataset</strong>, scegli il tipo di file che stai caricando
            (ad esempio «Building Load Profile»).</li>
          <li>Se richiesto, scegli a quale <strong>edificio</strong> appartiene il file.</li>
          <li>Trascina il file CSV nell'area di caricamento, oppure clicca <strong>Sfoglia file</strong>.</li>
          <li>Clicca <strong>Valida &amp; Anteprima</strong>.</li>
        </ol>
        <ScreenshotPlaceholder
          img="upload-page.png"
          description="The community Data page with a dataset type selected, showing the required-columns badges, the download-template button, and the drag-and-drop upload area."
          source="Platform — open any community → click 'Data Management' in the sidebar → pick a dataset type from the dropdown so the upload area appears. Capture the form area."
          caption="La pagina Gestione Dati: scegli un tipo, poi trascina il file."
        />
        <h5 className="mt-4">Leggere il risultato</h5>
        <ul>
          <li><strong>Banner verde</strong> — il file è valido. Mostra quante righe sono state lette e l'intervallo di date. Hai finito.</li>
          <li><strong>Avvisi gialli</strong> — il file è stato accettato ma c'è qualcosa da verificare (ad esempio ore mancanti). Puoi mantenerlo così o migliorare il file.</li>
          <li><strong>Banner rosso</strong> — il file è stato rifiutato. L'anteprima elenca ogni problema con il numero di riga corrispondente. Correggi il file e ricaricalo.</li>
        </ul>
        <ScreenshotPlaceholder
          img="upload-validation-result.png"
          description="The validation result panel after a successful upload — green success banner plus the data preview table."
          source="Platform — on the same Data Management page, after uploading a valid CSV and clicking 'Validate & Preview'. Capture the green banner and the first few rows of the preview table."
          caption="Un caricamento riuscito mostra un banner verde e un'anteprima."
        />
        <p>
          Ogni messaggio di errore rosso è spiegato in
          <em> «Perché il mio caricamento è fallito?»</em> nella sezione Risoluzione dei problemi.
        </p>
      </>
    ),
  },

  {
    id: 'data-load',
    category: 'data',
    title: 'File di consumo (carico) degli edifici',
    summary: 'Il file orario dell\'uso di elettricità — il file più importante per ogni edificio.',
    body: (
      <>
        <p>
          Un <strong>Building Load Profile</strong> registra quanta elettricità ha consumato un edificio,
          un valore per ogni ora. È la base per le previsioni e l'ottimizzazione, quindi preparane
          uno per ogni edificio se possibile.
        </p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonna</th><th>Obbligatoria?</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>Obbligatoria</td><td>L'ora, es. <Code>2025-01-01T08:00:00Z</Code></td></tr>
            <tr><td><Code>consumption_kwh</Code></td><td>Obbligatoria</td><td>Elettricità consumata in quell'ora, in kWh (un numero, decimali con punto, mai negativo)</td></tr>
            <tr><td><Code>peak_demand_kw</Code></td><td>Opzionale</td><td>Massima potenza assorbita in quell'ora, in kW</td></tr>
          </tbody>
        </Table>
        <p><strong>Esempio:</strong></p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,consumption_kwh
2025-01-01T00:00:00Z,0.457
2025-01-01T01:00:00Z,0.343
2025-01-01T02:00:00Z,0.298`}
        </pre>
        <p>Un valore per ora; un anno completo corrisponde a 8.760 righe. Sono necessarie almeno 24 righe (un giorno).</p>
        <TemplateBtn type="building_load_profile">Scarica il template del carico</TemplateBtn>
        <Warn>Il consumo non può mai essere negativo. Un valore negativo indica che una colonna è stata confusa — verifica che la generazione non sia stata inserita nella colonna del consumo.</Warn>
      </>
    ),
  },

  {
    id: 'data-generation',
    category: 'data',
    title: 'File di generazione solare ed eolica',
    summary: 'Produzione oraria di pannelli solari e turbine eoliche.',
    body: (
      <>
        <p>I file di generazione registrano quanta energia ha prodotto una fonte rinnovabile, un valore per ogni ora.</p>
        <h5 className="mt-3">Impianto solare o eolico comunitario</h5>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Colonna</th><th>Significato</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>timestamp</Code></td><td>L'ora, es. <Code>2025-01-01T12:00:00Z</Code></td></tr>
            <tr><td><Code>generation_kw</Code></td><td>Potenza prodotta in quell'ora, in kW (mai negativa — di notte il solare è <Code>0</Code>)</td></tr>
          </tbody>
        </Table>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,generation_kw
2025-01-01T00:00:00Z,0
2025-01-01T12:00:00Z,105.6
2025-01-01T18:00:00Z,12.3`}
        </pre>
        <TemplateBtn type="pv_generation_profile">Template solare (PV)</TemplateBtn>
        <TemplateBtn type="wind_generation_profile">Template eolico</TemplateBtn>

        <h5 className="mt-4">Solare su tetto di un singolo edificio</h5>
        <p>
          Per il solare che appartiene a un singolo edificio, la colonna del valore è
          <Code>generation_kwh</Code> (energia in kWh) invece di <Code>generation_kw</Code>.
          La piattaforma mostra il nome esatto della colonna attesa quando selezioni il tipo di dataset nella
          pagina di caricamento — segui sempre quanto indicato lì.
        </p>
        <Tip>Le ore senza sole o senza vento devono contenere <Code>0</Code>, non essere lasciate vuote.</Tip>
      </>
    ),
  },

  {
    id: 'data-tariff',
    category: 'data',
    title: 'File delle tariffe (prezzi dell\'elettricità)',
    summary: 'Prezzi dell\'elettricità per importazione ed esportazione — storici e Day-Ahead.',
    body: (
      <>
        <p>I file delle tariffe comunicano alla piattaforma il prezzo dell'elettricità per ogni ora.</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>File</th><th>Colonna del valore</th><th>Righe</th></tr>
          </thead>
          <tbody>
            <tr><td>Tariffa importazione storica</td><td><Code>import_price_eur_kwh</Code></td><td>Un anno, orario (≈8.760)</td></tr>
            <tr><td>Tariffa esportazione storica</td><td><Code>export_price_eur_kwh</Code></td><td>Un anno, orario (≈8.760)</td></tr>
            <tr><td>Tariffa importazione Day-Ahead</td><td><Code>import_price_eur_kwh</Code></td><td>24 (un giorno)</td></tr>
            <tr><td>Tariffa esportazione Day-Ahead</td><td><Code>export_price_eur_kwh</Code></td><td>24 (un giorno)</td></tr>
          </tbody>
        </Table>
        <p>
          Il prezzo di <strong>importazione</strong> è quanto paghi per acquistare elettricità; il prezzo di <strong>esportazione</strong>
          è quanto ti viene pagato per venderla. I prezzi sono in euro per kWh (es. <Code>0.22</Code>)
          e usano il punto come separatore decimale.
        </p>
        <pre style={{ background: 'var(--color-neutral-50)', padding: 12, borderRadius: 6, fontSize: '0.8rem' }}>
{`timestamp,import_price_eur_kwh
2025-01-01T00:00:00Z,0.08
2025-01-01T12:00:00Z,0.22
2025-01-01T19:00:00Z,0.28`}
        </pre>
        <TemplateBtn type="energy_tariff_data">Template tariffa importazione</TemplateBtn>
        <TemplateBtn type="feed_in_tariff_data">Template tariffa esportazione</TemplateBtn>
        <Tip>
          I file <strong>Day-Ahead</strong> coprono un solo giorno (24 righe). Caricane uno nuovo ogni giorno —
          i suoi timestamp devono corrispondere alla data di quel giorno. I template qui sopra funzionano anche come
          riferimento per il formato a 24 righe.
        </Tip>

        <h5 className="mt-4">Dove posso trovare i dati tariffari?</h5>
        <p>
          Con il termine «tariffa» si intendono due cose diverse — trattale separatamente:
        </p>
        <ul>
          <li><strong>Prezzi all'ingrosso / Day-Ahead</strong> — il prezzo orario di mercato pubblicato dalla
            borsa elettrica del tuo paese. Usalo per i file di tariffa <em>Day-Ahead</em> e come buona
            approssimazione dei contratti a prezzo variabile.</li>
          <li><strong>Tariffa al dettaglio</strong> — quanto paghi effettivamente (energia + oneri di rete + tasse).
            Ricavala dal contratto o dalla fattura del tuo fornitore.</li>
        </ul>

        <p className="mt-3"><strong>Fonte pan-europea (valida per tutti i paesi):</strong></p>
        <ul>
          <li><strong>ENTSO-E Transparency Platform</strong> —
            {' '}<Ext href="https://transparency.entsoe.eu">transparency.entsoe.eu</Ext>. Gratuita, ma
            richiede la registrazione. Apri <em>Market → Day-ahead Prices</em>, seleziona la tua zona di offerta
            (es. IT-North, FR, DE-LU, CZ, GR), l'intervallo di date e poi <em>Export Data</em> → CSV.
            La colonna dei prezzi è in <strong>€/MWh — dividi per 1000 per convertire in €/kWh</strong>
            (es. 220 €/MWh = 0,22 €/kWh).</li>
        </ul>

        <p className="mt-3"><strong>Fonti nazionali (spesso più semplici, senza registrazione):</strong></p>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Paese</th><th>Sito</th><th>Cosa si ottiene</th></tr>
          </thead>
          <tbody>
            <tr><td>Italia</td><td><Ext href="https://www.mercatoelettrico.org">mercatoelettrico.org</Ext> (GME)</td><td>Prezzo PUN Day-Ahead, orario. Cerca «Esiti MGP». €/MWh — dividi per 1000.</td></tr>
            <tr><td>Francia</td><td><Ext href="https://data.rte-france.com">data.rte-france.com</Ext> (RTE Open Data) o <Ext href="https://www.epexspot.com/en">epexspot.com</Ext></td><td>RTE: portale open data gratuito, prezzi Day-Ahead orari. EPEX: sito del mercato.</td></tr>
            <tr><td>Germania</td><td><Ext href="https://www.smard.de/en">smard.de</Ext> (Bundesnetzagentur)</td><td>«Market data» → «Wholesale prices» → «Day-ahead, Germany/Luxembourg». Download gratuito, senza registrazione.</td></tr>
            <tr><td>Rep. Ceca</td><td><Ext href="https://www.ote-cr.cz/en">ote-cr.cz</Ext> (OTE)</td><td>«Short-term markets» → «Day-ahead market». Esportazione CSV per giorno.</td></tr>
            <tr><td>Grecia</td><td><Ext href="https://www.enexgroup.gr/web/guest/markets-publications-el-day-ahead-market">enexgroup.gr</Ext> (HEnEx)</td><td>Risultati del Day-Ahead Market, PDF e CSV giornalieri. €/MWh — dividi per 1000.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Convertire un file di prezzi scaricato nel formato della piattaforma</h5>
        <p>
          I file esportati dalle borse elettriche raramente hanno esattamente i nomi di colonna richiesti dalla piattaforma. Segui questi
          passaggi per convertire uno qualsiasi dei file indicati sopra:
        </p>
        <ol>
          <li>Apri il CSV scaricato in Excel o Google Sheets.</li>
          <li>Individua la colonna data/ora e la colonna dei prezzi (le altre colonne possono essere eliminate).</li>
          <li>Crea una colonna chiamata <Code>timestamp</Code>. Se la fonte contiene già un timestamp ISO,
            copialo; altrimenti costruiscilo con la formula indicata in
            {' '}<ArticleLink to="data-csv-basics">Creare file CSV correttamente</ArticleLink>.</li>
          <li>Crea una colonna chiamata <Code>import_price_eur_kwh</Code>. Se la fonte è in €/MWh, dividi
            per 1000 con una formula come <Code>=B2/1000</Code> e incolla come valori.</li>
          <li>Salva come <strong>CSV UTF-8</strong> e carica come <em>Tariffa importazione storica</em>.</li>
        </ol>

        <Warn>
          Prezzo all'ingrosso ≠ tariffa al dettaglio. Il valore all'ingrosso è solo il prezzo del mercato energetico e
          non include oneri di rete, imposte o IVA. Per un'analisi dei costi accurata, preferisci una tariffa
          al dettaglio del tuo fornitore, oppure combina il prezzo all'ingrosso con un costo fisso aggiuntivo noto.
        </Warn>

        <h5 className="mt-4">Tariffa di esportazione (feed-in)</h5>
        <p>
          I prezzi di esportazione dipendono dal regime nazionale — possono essere una tariffa feed-in fissa, il
          prezzo all'ingrosso meno uno sconto, oppure zero. Ottieni la regola dal tuo fornitore, dal tuo DSO o
          dal regolatore nazionale (ARERA in Italia, CRE in Francia, BNetzA in Germania, ERÚ in Repubblica Ceca,
          RAE in Grecia) e inserisci il valore €/kWh risultante per ogni ora.
        </p>
      </>
    ),
  },
]
